/* eslint-disable @typescript-eslint/naming-convention */
import {
  Component,
  Input,
  Output,
  OnChanges,
  EventEmitter,
} from '@angular/core';
import { ElapsedPipe } from '../elapsed.pipe';
import { Activity, Lap } from '../models';
import Rainbow from 'rainbowvis.js';
import tinycolor from 'tinycolor2';
import { HelperService } from 'src/app/services/helper.service';

declare let Chart;
interface ChartLap extends Lap {
  newpace: number;
}

@Component({
  selector: 'app-strava-graph',
  templateUrl: './strava-graph.component.html',
  providers: [ElapsedPipe],
})
export class StravaGraphComponent implements OnChanges {
  @Input() activity: Activity;
  @Input() selectedLaps: ChartLap[] | Lap[];
  @Input() ticksX = false;
  @Input() ticksY = true;
  @Input() palette;
  @Input() colorIndex = 0;
  @Input() height = 25;
  @Input() width = '100vw';
  // eslint-disable-next-line max-len
  @Input() selectedColor =
    'linear-gradient(90deg,blue 0%, rgba(14,251,31,1) 33%, rgba(243,255,0,1) 66%, rgba(255,0,0,1) 100%)';
  @Input() reverseSelected = 'black';
  @Output() exportImage = new EventEmitter<HTMLCanvasElement>();
  mostFrequent = undefined;
  max;
  defaultImperial =
    localStorage.getItem('imperial') &&
    localStorage.getItem('imperial') === 'true';
  isImperial = false;
  barChart: any;
  laps;

  constructor(
    private elapsed: ElapsedPipe,
    private helperService: HelperService
  ) {}

  ngOnChanges() {
    this.laps = [...this.selectedLaps];
    this.mostFrequent = this.getMostFrequent(
      this.laps.filter((lap) => lap.distance > 0).map((lap) => lap.distance)
    );
    this.max = Math.max(
      ...this.laps
        .filter((lap) => lap.distance === this.mostFrequent)
        .map((lap) => lap.pace)
    );
    if (this.barChart) {
      this.updateGraph();
    } else {
      this.generateGraph();
    }
  }

  getMostFrequent(arr) {
    let lapsFound = false;
    const hashmap = arr.reduce((acc, val) => {
      acc[val] = (acc[val] || 0) + 1;
      if (acc[val] > 1 && !lapsFound) {
        lapsFound = true;
      }
      return acc;
    }, {});
    if (lapsFound) {
      return +Object.keys(hashmap).reduce((a, b) =>
        hashmap[a] > hashmap[b] ? a : b
      );
    }
    return undefined;
  }

  getScale(difference: number): number {
    const scales = [5, 10, 15, 20, 25];
    if (difference > 30) {
      return 30;
    } else {
      return scales.reduce((prev, curr) =>
        Math.abs(curr - difference) < Math.abs(prev - difference) ? curr : prev
      );
    }
  }

  checkDiff(array) {
    return new Set(array).size === 1;
  }

  getContext(context, max) {
    if (context.datasetIndex === 1) {
      return context.raw;
    }
    return this.elapsed.transform(-+context.raw + max + 60);
  }

  generateGraph() {
    let min = Math.min(...this.laps.map((lap) => lap.pace));
    let max = Math.max(...this.laps.map((lap) => lap.pace));
    this.laps.forEach((lap) => {
      if (this.mostFrequent) {
        max = this.max + 60;
        if (lap.pace > max) {
          lap.newpace = max;
        } else {
          lap.newpace = lap.pace;
        }
      } else {
        lap.newpace = lap.pace;
      }
    });
    max = max + (5 - (max % 5));
    min = min - (5 - (min % 5));
    const stepSize = this.getScale(max - min);
    this.colorGenerator();
    this.barChart = new Chart('stravaChart', {
      type: 'bar',
      data: {
        labels: this.laps.map((lap, index) => {
          if (index === 0) {
            return lap.name;
          }
          return lap.name.replace('Lap ', '');
        }),
        datasets: [
          {
            label: 'Laps',
            data: this.laps.map((lap) => max + 5 - lap.newpace),
            backgroundColor: this.colorGenerator(),
            //borderColor:'rgba(255,255,255,0)',
            order: 2,
            //borderWidth: 1,
            barThickness: 'flex',
            setPercentage: this.getColumnsWidth(),
            yAxisID: 'y',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'x',
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => this.getContext(context, max),
            },
          },
        },
        scales: {
          y: {
            // min: max - min -stepSize,
            type: 'linear',
            grace: 5,
            position: 'left',
            ticks: {
              font: {
                family: 'Barlow Semi Condensed',
              },
              display: this.ticksY,
              precision: 0,
              callback: (value: number, index, ticks) => {
                if (index > 0 && index < ticks.length - 1) {
                  return this.elapsed.transform(max + 5 - value);
                }
              },
              stepSize,
            },
            grid: {
              drawBorder: this.ticksY,
              display: this.ticksY,
              drawTicks: false,
            },
          },
          x: {
            ticks: {
              font: {
                family: 'Barlow Semi Condensed',
              },
              align: 'start',
              maxRotation: 0,
              minRotation: 0,
              display: this.ticksX,
            },
            grid: {
              drawBorder: this.ticksY,
              drawTicks: false,
              display: false,
            },
          },
        },
      },
    });
  }

  updateGraph() {
    let min = Math.min(...this.laps.map((lap) => lap.pace));
    let max = Math.max(...this.laps.map((lap) => lap.pace));
    this.laps.forEach((lap) => {
      if (this.mostFrequent) {
        max = this.max + 60;
        if (lap.pace > max) {
          lap.newpace = max;
        } else {
          lap.newpace = lap.pace;
        }
      } else {
        lap.newpace = lap.pace;
      }
    });
    max = max + (5 - (max % 5));
    min = min - (5 - (min % 5));
    const stepSize = this.getScale(max - min);
    this.barChart.data.labels = this.laps.map((lap) => lap.name);
    this.barChart.data.datasets[0].data = this.laps.map(
      (lap) => max + 5 - lap.newpace
    );
    this.barChart.data.datasets[0].backgroundColor = this.colorGenerator();
    this.barChart.data.datasets[0].borderColor = this.reverseSelected;
    this.barChart.data.datasets[0].setPercentage = this.getColumnsWidth();
    this.barChart.options = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'x',
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => this.getContext(context, max),
          },
        },
      },
      scales: {
        y: {
          type: 'linear',
          grace: 5,
          position: 'left',
          ticks: {
            font: {
              family: 'Barlow Semi Condensed',
            },
            display: this.ticksY,
            precision: 0,
            callback: (value: number, index, ticks) => {
              if (index > 0 && index < ticks.length - 1) {
                return this.elapsed.transform(max + 5 - value);
              }
            },
            stepSize,
          },
          grid: {
            drawBorder: false,
            display: this.ticksY,
            drawTicks: false,
          },
        },
        x: {
          ticks: {
             font: {
                family: 'Barlow Semi Condensed',
              },
            maxRotation: 0,
            minRotation: 0,
            display: this.ticksX,
          },
          grid: {
            drawTicks: false,
            display: false,
          },
        },
      },
    };
    this.barChart.update();
  }

  getColumnsWidth() {
    const x = [];
    this.laps.forEach((lap) =>
      x.push((lap.distance * 100) / this.activity.distance)
    );
    return x;
  }

  export() {
    this.exportImage.emit(
      document.getElementById('stravaChart') as HTMLCanvasElement
    );
  }

  colorGenerator() {
    const sortFn = (n1, n2) => n1 - n2;
    const orderedSet = [...this.laps.map((lap) => lap.newpace)].sort(sortFn);
    const uniqueValues = [...new Set(orderedSet)];
    const rainbow = new Rainbow();
    if (this.selectedColor.indexOf('linear') > -1) {
      const lapEfrort = Math.round(
        (Math.max(...uniqueValues) - Math.min(...uniqueValues)) / 15
      );
      if (lapEfrort > 6) {
        rainbow.setSpectrum('red', 'orange', 'yellow', 'lime', '#0059a7');
      } else if (lapEfrort > 4 && lapEfrort <= 6) {
        rainbow.setSpectrum('red', 'orange', 'yellow', 'lime');
      } else {
        rainbow.setSpectrum('red', 'orange', 'yellow');
      }
    } else {
      const hexFront = tinycolor(this.selectedColor);
      rainbow.setSpectrum(
        this.selectedColor,
        this.helperService.lightenColor(hexFront.toHex())
      );
    }
    rainbow.setNumberRange(0, uniqueValues.length);
    const colorMap = {};
    uniqueValues.forEach((item, index) => {
      colorMap[item] = rainbow.colorAt(index);
    });
    const newColorArray = [];
    this.selectedLaps
      .map((lap) => lap.newpace)
      .forEach((item) => {
        if (colorMap[item]) {
          newColorArray.push('#' + colorMap[item]);
        } else {
          newColorArray.push('lightgreen');
        }
      });
    return newColorArray;
  }
}
